import React, { useEffect, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import PropTypes from 'prop-types';
import isArray from 'lodash/isArray';

import LoadMask from 'components/LoadMask';
import ShareList from 'components/ShareList';
import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicSubmitButton from 'components/BasicSubmitButton';
import BasicButton from 'components/BasicButton';
import HorizontalLoader from 'components/HorizontalLoader';

import { useApi } from 'hooks/useApi';
import { useSelection } from 'hooks/useSelection';
import { ENDPOINT } from 'constants/endpoints';
import { ERROR_FIELD } from 'constants/error-fields';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation';

import './styles.css';

const ShortlistShareDialogBody = props => {
  const { id, proceed, dismiss, cancel } = props;
  const api = useApi();

  const [state, setState] = useState({ init: false, ids: [], rows: [] });
  const [isSubmitting, setSubmitting] = useState(false);

  const [{ isSelected, select, deselect, getSelectedIds }] = useSelection(
    state.ids
  );

  useEffect(() => {
    api.get(
      ENDPOINT.LIST.SHORTLIST_TEAM,
      { id },
      {
        handleError: response => handleError(response),
        handleSuccess: response => {
          if (response.rows.length) {
            setState({
              init: true,
              ...response,
              ids: response.rows.map(r => {
                r.Selected === 'Yes' && select(r.RecruiterID);
                return r.RecruiterID;
              }),
            });
          } else {
            cancel({
              message: (
                <>
                  You can't share your Shortlist.
                  <br />
                  There are no active Team Members on your team
                </>
              ),
            });
          }
        },
      }
    );
  }, []);

  const handleError = (response, setErrors) => {
    const {
      data: { fields },
    } = response;
    if (fields && typeof setErrors === 'function') {
      const obj = {};
      for (let [key, value] of Object.entries(fields)) {
        const error = isArray(value) ? value[0] : value;
        obj[key] = ERROR_FIELD[error] || `${error}`;
      }
      setErrors(obj);
    } else {
      cancel(response);
    }
  };

  const handleSubmit = () => {
    setSubmitting(true);
    api.post(
      ENDPOINT.ACTION.SHORTLIST.SHARE,
      { id, recruiters: getSelectedIds() },
      {
        handleError: response => handleError(response),
        handleSuccess: () => proceed(),
      }
    );
  };

  return state.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <div className="form__content shortlist-share-form">
          {isSubmitting && <LoadMask />}

          <div className="shortlist-share-form__content">
            <ShareList
              data={state.rows}
              actions={{
                onSelect: select,
                onDeselect: deselect,
                isSelected: isSelected,
              }}
            />
          </div>

          <div className="form__controls form__controls--compact">
            <BasicButtonGroup>
              <BasicSubmitButton
                controlFunc={handleSubmit}
                btnWidth={150}
                btnHeight={40}
                btnFontWeight={700}
                btnFontSize="0.8571rem"
                btnArrow={false}
                btnTextColor="#27282d"
                btnText="Share"
              />

              <BasicButton
                controlFunc={dismiss}
                btnWidth={150}
                btnHeight={40}
                btnFontWeight={700}
                btnFontSize="0.8571rem"
                btnArrow={false}
                btnTextColor="#27282d"
                btnText="Cancel"
              />
            </BasicButtonGroup>
          </div>
        </div>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <div className="horizontal-loader__container">
          <HorizontalLoader />
        </div>
      </div>
    </div>
  );
};

ShortlistShareDialogBody.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  proceed: PropTypes.func.isRequired,
  dismiss: PropTypes.func.isRequired,
  cancel: PropTypes.func.isRequired,
};

export default ShortlistShareDialogBody;
